import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { SignIn, useAuth } from '@clerk/react';
import { Shield } from 'lucide-react';

/**
 * Reviewer sign-in — a distinct entry point (separate from staff SSO at /login) for
 * authenticated external reviewers (Group B: advisors/auditors invited to a specific claim).
 * Clerk's email-code sign-in strategy is what the reviewer-invite email links here to; which
 * strategies are actually offered (email code vs. password vs. SSO) is controlled by the Clerk
 * Dashboard for this instance, not by this component.
 *
 * Dark theme matching the existing anonymous audit portal (see portal-shell.tsx) — no main-app
 * navigation is rendered here or after sign-in (reviewers never see the staff shell).
 */
export function ReviewerLoginPage() {
  const { isSignedIn, isLoaded } = useAuth();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      navigate('/reviewer');
    }
  }, [isLoaded, isSignedIn, navigate]);

  return (
    <div className="dark min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <Shield className="h-10 w-10 text-blue-400 mx-auto" aria-hidden="true" />
          <h1 className="text-xl font-semibold text-white">Edison Reviewer Sign-In</h1>
          <p className="text-sm text-gray-400">
            Sign in with the email address your invitation was sent to.
          </p>
        </div>

        <SignIn
          routing="hash"
          forceRedirectUrl="/reviewer"
          appearance={{
            elements: {
              rootBox: 'w-full',
              card: 'shadow-none border border-gray-800 bg-gray-900 rounded-lg',
              headerTitle: 'text-white',
              headerSubtitle: 'text-gray-400',
              // Hide all social/OAuth buttons — reviewers sign in with email code only
              socialButtonsBlockButton: 'hidden',
              socialButtonsBlock: 'hidden',
              dividerRow: 'hidden',
              dividerText: 'hidden',
              formFieldLabel: 'text-gray-300',
              formFieldInput: 'bg-gray-950 border-gray-700 text-white',
              footerActionText: 'text-gray-500',
              footerActionLink: 'text-blue-400',
            },
          }}
        />

        <p className="text-xs text-gray-600 text-center">
          Access is by invitation only, scoped to a specific R&D claim. All access is logged for
          audit purposes.
        </p>
      </div>
    </div>
  );
}
